import { Form, Space, Typography } from "antd";
import React from "react";
import SelectBox from "../SelectBox/SelectBox";
import BankAccountSVG from "@images/funds/bank-account.svg";
import WalletSVG from "@images/funds/empty-wallet.svg";
import {
  WithdrawalProcessSteps,
  useWithdrawalProcess,
} from "../../useWithdrawalProcess";
import { useFormik } from "formik";
import * as Yup from "yup";
import { IWithdrawFees } from "@funds/components/WithdrawForm/IWithdrawForm";
import { TReducers } from "@features/reducers";
import { connect } from "react-redux";
import {
  getWalletFee,
  getWalletFeeObject,
} from "@funds/features/helpers/validation";
import { CRYPTO_PLACES } from "@appComponents/CurrencyElement/CurrencyElement";
import Big from "big.js";
import { MAIN_CURRENCY, mainCurrencies } from "@bundles/consts";
import { IPrivateWallets } from "@funds/components/WithdrawAmount/IWithdrawAmount";

import * as styles from "./SelectMethodStyle.module.scss";

type Props = {
  fiatWithdrawalBlocked: number;
  fees: IWithdrawFees[];
  exchangeRate: number | undefined;
  currency: string;
  cryptoWallets: IPrivateWallets[];
  companyInvestorId?: number;
};

const SelectMethod = ({
  fiatWithdrawalBlocked,
  fees,
  exchangeRate,
  currency,
  cryptoWallets,
  companyInvestorId,
}: Props) => {
  const { state, setState } = useWithdrawalProcess();

  const { selectedMethod } = state;

  const formik = useFormik({
    initialValues: {
      selectedMethod,
    },
    onSubmit: (values) => {},
    validationSchema: Yup.object({
      selectedMethod: Yup.string().required().oneOf(["crypto", "bankTransfer"]),
    }),
  });

  const currencyLabel =
    currency === "bfusd" ? "USDT" : currency?.toLocaleUpperCase();

  const walletsCurrencies = cryptoWallets.map((el) => el.currency);
  const transactionFees = fees.filter((el) =>
    walletsCurrencies.includes(el.currency)
  );
  const minimumAmounts = transactionFees.map((el) => el.minimum_amount);
  const flatFees = transactionFees.map((el) => el.fees.flat_fee);

  const selectedMinimumAmount = Math.min(...minimumAmounts);
  const flatFee = Math.min(...flatFees);

  const selectedWalletFeeObject = getWalletFeeObject(fees, currency);
  const selectedWalletFee = getWalletFee(selectedWalletFeeObject, 0);
  const defaultMinimumAmount =
    fees?.find((fee: any) => fee.currency === "default")?.minimum_amount || 0;

  const defaultMinimumAmountSetting = exchangeRate
    ? Big(defaultMinimumAmount).div(exchangeRate)
    : 0;

  const minimumAmountSetting =
    selectedWalletFeeObject && selectedWalletFeeObject.currency !== "default"
      ? selectedWalletFeeObject.minimum_amount
      : defaultMinimumAmountSetting;

  const minimumAmount = Big(minimumAmountSetting)
    .plus(selectedWalletFee)
    .round(CRYPTO_PLACES);

  const isMainCurrency = mainCurrencies.includes(currency ?? "");

  const fiatSelectedWalletFeeObject = isMainCurrency
    ? fees?.find((fee: any) => fee.currency === currency)
    : undefined;

  const cryptoTransactionFee = isFinite(flatFee) ? flatFee : selectedWalletFee;
  const cryptoMinimumAmount = isFinite(selectedMinimumAmount)
    ? Big(selectedMinimumAmount ?? 0)
    : minimumAmount;

  return (
    <Form onFinish={formik.handleSubmit}>
      <Space className={styles.selectMethod} direction="vertical">
        <SelectBox
          onClick={() => {
            formik.setFieldValue("selectedMethod", "crypto");
            setState({
              ...state,
              selectedMethod: "crypto",
              step: WithdrawalProcessSteps.WITHDRAWAL_CRYPTO,
            });
          }}
          icon={<WalletSVG width={36} />}
          title="Crypto Wallets"
          footer={[
            {
              label: <Typography>ESTIMATED TRANSACTION FEE:</Typography>,
              value: Big(cryptoTransactionFee).eq(0)
                ? "Blockchain fees"
                : `${Big(cryptoTransactionFee).round(
                    isMainCurrency ? 2 : CRYPTO_PLACES
                  )} ${currencyLabel}`,
            },
            {
              label: <Typography>MINIMUM AMOUNT:</Typography>,
              value: `${cryptoMinimumAmount.round(
                isMainCurrency ? 2 : CRYPTO_PLACES
              )} ${currencyLabel}`,
            },
          ]}
        />

        {isMainCurrency && !companyInvestorId && (
          <SelectBox
            icon={<BankAccountSVG className={styles.bankAccountSvg} />}
            title="Bank Account"
            disabled={!isMainCurrency || fiatWithdrawalBlocked === 1}
            onClick={() => {
              formik.setFieldValue("selectedMethod", "bankTransfer");
              setState({
                ...state,
                selectedMethod: "bankTransfer",
                step: WithdrawalProcessSteps.WITHDRAWAL_BANK_TRANSFER,
              });
            }}
            footer={[
              {
                label: <Typography>TRANSACTION FEE:</Typography>,
                value: "1% (min. $100) ",
              },
              {
                label: <Typography>MINIMUM AMOUNT:</Typography>,
                value: `${Big(
                  fiatSelectedWalletFeeObject?.minimum_amount ?? 0
                ).round(2)} ${currencyLabel}`,
              },
            ]}
          />
        )}
      </Space>
    </Form>
  );
};

const mapStateToProps = (store: TReducers, ownProps: any) => {
  return {
    fiatWithdrawalBlocked: store.appSettings.fiat_withdraw_blocked,
    cryptoWallets: (store.fundsReducer.cryptoWallets || []).filter(
      (el) => el.supported_withdraw_currency === ownProps.currency
    ),
    companyInvestorId: store.companyReducer.companyDetails.investor_id,
  };
};

export default connect(mapStateToProps)(SelectMethod);
